<script lang="ts" setup generic="T extends string | Component">
import { computed, useCssModule, useSlots, h, mergeProps, useAttrs, type Component, type VNodeChild } from 'vue';
import type { PolymorphicProps } from '../../shared/types';

const props = defineProps<
  PolymorphicProps<T> & {
    disabled?: boolean;
    active?: boolean;
    block?: boolean;
    size?: 'm' | 'l';
    variant?: 'primary' | 'secondary' | 'tertiary';
  }
>();

defineSlots<{
  default?: () => VNodeChild;
  leadingVisual?: () => VNodeChild;
  trailingVisual?: () => VNodeChild;
}>();

const style = useCssModule();

const rootProps = computed(() => {
  return {
    class: [
      style.root,
      {
        [style.sizeL]: props.size === 'l',
        [style.variantSecondary]: props.variant === 'secondary',
        [style.variantTertiary]: props.variant === 'tertiary',
        [style.disabled]: props.disabled,
        [style.active]: props.active,
        [style.block]: props.block,
      },
    ],
    'data-ui-component': 'UiButton',
  };
});

const slots = useSlots();
const attrs = useAttrs();

defineRender(() => {
  const children = {
    default: () => {
      const content = slots.default?.() ?? [];
      const leadingVisual = slots.leadingVisual?.();
      const trailingVisual = slots.trailingVisual?.();

      return [
        leadingVisual ? h('div', { class: style.leadingVisual }, leadingVisual) : null,
        ...content,
        trailingVisual ? h('div', { class: style.trailingVisual }, trailingVisual) : null,
      ];
    },
  };

  const computedProps = mergeProps(attrs, rootProps.value, props.as && props.asProps ? props.asProps : {});

  if (props.as) {
    return h(props.as, computedProps, children);
  }

  return h('button', { type: 'button', ...computedProps, disabled: props.disabled }, children);
});
</script>

<style lang="scss" module>
@use '../../styles/shared';

.root {
  @include shared.reset-button();
  position: relative;
  display: inline-flex;
  gap: 4px;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  border-radius: 4px;
  height: 28px;
  flex: none;
  background: #226b6c;
  color: #f5f5f5;
  border: 1px solid #226b6c;
  padding: 0 (8px - 1px); // border
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
  }

  &:hover:not(.disabled) {
    background: linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%), #226b6c;
  }

  &:active:not(.disabled),
  &.active:not(.disabled) {
    background:
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%),
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%), #226b6c;
  }

  &:disabled,
  &.disabled {
    color: #d2d3d9;
    background: #ebecf2;
    border-color: #ebecf2;
    cursor: not-allowed;
  }
}

.leadingVisual,
.trailingVisual {
  width: 18px;
  height: 18px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  > img,
  > svg {
    max-width: 100%;
    max-height: 100%;
  }
}

.block {
  width: 100%;
}

.sizeL {
  height: 36px;
  padding: 0 (16px - 1px); // border
}

.variantSecondary,
.variantTertiary {
  color: #010000;
  background: transparent;

  &:hover:not(.disabled) {
    background: rgba(35, 57, 93, 0.05);
  }

  &:active:not(.disabled),
  &.active:not(.disabled) {
    background: rgba(35, 57, 93, 0.1);
    color: #226b6c;
  }
}

.variantSecondary {
  border-color: #d5d6d9;
}

.variantTertiary {
  border-color: transparent;

  &.disabled {
    background: transparent;
    border-color: transparent;
  }
}
</style>
